import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';

import clsx from 'clsx';
import { withStyles } from '@material-ui/core';
import SignDocumentDialog from '@componentsV2/SignDocumentDialog';
import { AppSidebar } from '@/components/ui/app-sidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import useFetchEffect from '@/hooks/useFetchEffect';
import useDesktop from '@/hooks/useDesktop';
import useAddNewDocumentMenu from '@/hooks/useAddNewDocumentMenu';
import { FileUploadContext } from '@/contexts/fileUpload';
import { fetchProperties as apiFetchProperties } from '@/api/properties';
import { fetchDocumentsToSigned } from '@/api/documents';
import { isPropertyOwnerNotFoundError } from '@/utils/requests';
import Search from '@/assets/icons/Search';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import AddNewDocumentMenu from '@/components/AddNewDocumentMenu';
import { useBoligmappaPlusSubscriptionStatus } from '@/store/hooks/useBoligmappaPlusSubscriptionStatus';
import AddFolder from '@/assets/icons/AddFolder';
import AddFile from '@/assets/icons/AddFile';
import UploadFolder from '@/assets/icons/UploadFolder';
import {
  sGetPersonalFoldersBreadCrumbs,
  sGetPersonalSelectedFolderId,
} from '@/store/reducers/personalDocuments';
import GlobalDashboard from '@/views/PrivateRoutes/GlobalDashboard';
import Notifications from '@/views/PrivateRoutes/Dashboard/Notifications';
import Notification from '@/views/PrivateRoutes/Dashboard/Notifications/Notification';
import ProtectedRoute from '@/components/ProtectedRoute';
import DashboardLayout from '../../../components/layouts/Dashboard';
import BoligmappaPlusPage from './BoligmappaPlusPage';
import Plus from '../../../assets/icons/raw/plus';
import PersonalAreaLandingPage from './PersonalAreaLandingPage';
import PersonalDocumentsPage from './PersonalDocumentsPage';
import Document from '../../PrivateRoutes/Dashboard/Documents/Document';
import ContractTemplatePage from './ContractTemplatePage';
import styles from './style';
import ProfileDelete from '../../PrivateRoutes/Dashboard/More/ProfileDelete';
import Terms from '../../PrivateRoutes/Dashboard/More/Terms';
import Consents from '../../PrivateRoutes/Dashboard/More/Consents';
import DownloadPersonalData from '../../PrivateRoutes/Dashboard/More/DownloadPersonalData';
import Account from '../../PrivateRoutes/Dashboard/More/Account';
import ContactUs from '../../PrivateRoutes/Dashboard/More/ContactUs';
import PrivateShare from '../../PrivateRoutes/Dashboard/More/PrivateShare';
import BoligmappaPlusLoyaltyPage from '../../PrivateRoutes/Dashboard/BoligmappaPlusLoyaltyPage';
import BoligmappaPlusLoyaltyCompanyPage from '../../PrivateRoutes/Dashboard/BoligmappaPlusLoyaltyCompanyPage';
import { locationsPageTitles } from '../../PrivateRoutes/Dashboard/util';
import PurchasesPage from '../../PrivateRoutes/Dashboard/PurchasesPage';
import {
  sGetActiveProperty,
  sGetDialogToSignDocSeen,
  sGetDocumentsToSigned,
  sGetOwnedProperties,
  sGetPropertiesLoaded,
  sGetSharedPrivatelyProperties,
} from '../../../store/reducers/properties';
import { sGetUser } from '../../../store/reducers/auth';
import { sGetUnseenNotifications } from '../../../store/reducers/notifications';
import {
  acSetDialogToSignDocSeen,
  acSetDocumentsToSigned,
  acSetFetchingDocumentsToSigned,
  acSetFetchingProperties,
  acSetFetchingPropertiesError,
  acSetProperties,
} from '../../../store/actions/properties';
import { sGetDocumentSearchText } from '../../../store/reducers/documents';
import {
  acClearDocuments,
  acSetDocumentSearchText,
} from '../../../store/actions/documents';

const Dashboard = ({
  classes,
  clearDocuments,
  ownedProperties,
  sharedProperties,
  activeUser,
  activeProperty,
  documentsToSigned,
  dialogToSignDocSeen,
  setDocumentsToSigned,
  setDialogToSignDocSeen,
  setFetchingDocumentsToSigned,
  searchText,
  setSearchText,
  selectedPersonalFolderId,
  personalFoldersBreadCrumbs,
  unseenNotifications,
  propertiesLoaded,
  setProperties,
  setFetchingProperties,
  setFetchingPropertiesError,
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isDesktop = useDesktop();
  const [isCollapsedSidebar, setIsCollapsedSidebar] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [searchVisible, setSearchVisible] = useState(false);
  const { onFileUploadClick } = useContext(FileUploadContext);
  const isBoligmappaPlusUser = useBoligmappaPlusSubscriptionStatus();

  const [activeSignDialogIndex, setActiveSignDialogIndex] = useState(0);

  const onDialogToSignClose = (el, index) => {
    setActiveSignDialogIndex(
      index + 1 <
        documentsToSigned.filter(
          (doc) => !dialogToSignDocSeen[doc?.boligmappaNumber]
        ).length
        ? index + 1
        : null
    );
    const dialogToSignSeen = Cookies.get('dialogToSignDocSeen');
    const parsedCookie = dialogToSignSeen ? JSON.parse(dialogToSignSeen) : {};
    const cookieValue = {
      ...parsedCookie,
      [el.boligmappaNumber]: true,
    };
    setDialogToSignDocSeen(cookieValue);
    Cookies.set('dialogToSignDocSeen', cookieValue, { expires: 14 });
  };

  const localActiveProperty =
    (activeProperty?.boligmappaNumber && activeProperty) ||
    (ownedProperties?.length > 0 && ownedProperties[0]) ||
    (sharedProperties?.length > 0 && sharedProperties[0]);

  useFetchEffect({
    dependencies: [propertiesLoaded],
    apiFetchFunction: apiFetchProperties,
    conditionFunction: ([_propertiesLoaded]) => !_propertiesLoaded,

    onFetchStart: () => setFetchingProperties(true),
    onFetchComplete: () => setFetchingProperties(false),

    onSuccess: (data) => setProperties(data),
    onError: (error) =>
      isPropertyOwnerNotFoundError(error)
        ? history.push('/signup')
        : setFetchingPropertiesError(error),
  });

  useEffect(() => {
    const titleKeys = Object.keys(locationsPageTitles());
    const selectedPageTitleKey = titleKeys.find((keyword) =>
      pathname.includes(keyword)
    );
    setPageTitle(locationsPageTitles()[selectedPageTitleKey] || '');
    const searchPages = ['documents', 'history', 'events', 'rooms'];
    setSearchVisible(
      searchPages.some((pageString) => pathname.includes(pageString))
    );
  }, [pathname]);

  useFetchEffect({
    setFetchingFunction: setFetchingDocumentsToSigned,
    onSuccess: (data) => setDocumentsToSigned(data?.documents),
    conditionFunction: () => !documentsToSigned,
    apiFetchFunction: () => fetchDocumentsToSigned(),
  });

  const {
    isCreatingName,
    setIsCreatingName,
    newFolderName,
    setNewFolderName,
    isAddingNewDoc,
    setIsAddingNewDoc,
    folderNameErrorMessage,
    isFolderNameError,
    onFolderCreate,
    onCancelCreatingFolder,
  } = useAddNewDocumentMenu({
    currentDocFolderId: selectedPersonalFolderId,
    breadCrumbs: personalFoldersBreadCrumbs,
    isPersonal: true,
  });
  const handleDocumentUpload = () => {
    onFileUploadClick({
      query: {
        bulkUploadMode: true,
        source: isDesktop ? 'table' : 'listView',
        newUpload: true,
      },
    });
    setIsAddingNewDoc(false);
  };

  const onFolderUpload = () => {
    onFileUploadClick({
      query: {
        bulkUploadMode: true,
        source: 'table',
        newUpload: true,
        isRoomOrEvent: true,
        webkitdirectory: true,
        isPersonal: true,
        parentDirectoryId: selectedPersonalFolderId,
        breadCrumbs: personalFoldersBreadCrumbs,
      },
    });
    setIsAddingNewDoc(false);
  };

  useEffect(() => {
    clearDocuments();
  }, []);

  return (
    <SidebarProvider>
      {isDesktop && (
        <AppSidebar
          ownedProperties={ownedProperties}
          sharedProperties={sharedProperties}
          activeProperty={localActiveProperty}
          unseenNotifications={unseenNotifications}
          activeUser={activeUser}
          documentsToSigned={documentsToSigned}
        />
      )}
      {isDesktop && (
        <div className="fixed flex justify-end pl-2 pr-2 font-circular bg-sidebar-primary-foreground w-full h-[50px] right-0 z-[99] border-b border-sidebar-border group-data-[collapsible=offcanvas]:w-0">
          <div
            className={`grid grid-cols-3 gap-4 duration-200 items-center ${
              isCollapsedSidebar ? 'w-full' : 'w-[calc(100%-16rem)]'
            }`}
          >
            <div className="flex items-center">
              <SidebarTrigger
                onClick={() => setIsCollapsedSidebar(!isCollapsedSidebar)}
              />
              <p className="text-xs font-semibold">{pageTitle}</p>
            </div>
            {searchVisible && (
              <>
                <div className={clsx([classes.searchWrapper, 'max-w-sm'])}>
                  <Search className={classes.searchIcon} />
                  <Input
                    className="h-8 pl-8 shadow-none"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={`Søk etter ${pageTitle.toLowerCase()}`}
                  />
                </div>
                <DropdownMenu
                  modal={false}
                  open={isAddingNewDoc}
                  onOpenChange={setIsAddingNewDoc}
                >
                  <DropdownMenuTrigger disabled={!isBoligmappaPlusUser} asChild>
                    <Button className="max-w-24 h-8 justify-self-end">
                      <Plus /> Legg til
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-[100px] min-w-56 rounded-lg"
                    align="end"
                    sideOffset={4}
                  >
                    <AddNewDocumentMenu
                      isCreatingName={isCreatingName}
                      setIsCreatingName={setIsCreatingName}
                      newFolderName={newFolderName}
                      setNewFolderName={setNewFolderName}
                      isFolderNameError={isFolderNameError}
                      folderNameErrorMessage={folderNameErrorMessage}
                      onCancelCreatingFolder={onCancelCreatingFolder}
                      onFolderCreate={onFolderCreate}
                    >
                      <AddFolder />
                      <p>Ny mappe</p>
                    </AddNewDocumentMenu>
                    <DropdownMenuItem
                      onClick={handleDocumentUpload}
                      className="cursor-pointer"
                    >
                      <AddFile />
                      <p>Last opp fil</p>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={onFolderUpload}
                      className="cursor-pointer"
                    >
                      <UploadFolder />
                      <p>Last opp mappe</p>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      )}
      <DashboardLayout
        documentsToSigned={documentsToSigned}
        allProperties={[
          ...(ownedProperties || []),
          ...(sharedProperties || []),
        ]}
        ownedProperties={ownedProperties}
        activeProperty={activeProperty}
        addButtonPaddingEnabled={false}
        backgroundContentContainer="#FAFAFA"
        className={
          !isDesktop || isCollapsedSidebar ? 'w-full' : 'w-[calc(100%-16rem)]'
        }
      >
        <ProtectedRoute
          component={GlobalDashboard}
          path="/dashboard/overview"
          exact
        />
        <Route path="/personal-area" exact>
          <Redirect to="/personal-area/dashboard/boligmappa-plus" />
        </Route>
        <Route
          component={PersonalAreaLandingPage}
          path="/personal-area/dashboard/home"
        />
        <Route
          exact
          component={PersonalDocumentsPage}
          path="/personal-area/dashboard/documents"
        />
        <Route
          exact
          path="/personal-area/dashboard/documents/:documentId"
          component={Document}
        />
        <Route
          exact
          component={BoligmappaPlusPage}
          path="/personal-area/dashboard/boligmappa-plus"
        />
        <Route
          component={ContractTemplatePage}
          path="/personal-area/dashboard/contract-templates"
        />
        <Route
          exact
          component={BoligmappaPlusLoyaltyPage}
          path="/personal-area/dashboard/boligmappa-plus/loyalty"
        />
        <Route
          exact
          component={BoligmappaPlusLoyaltyCompanyPage}
          path="/personal-area/dashboard/boligmappa-plus/loyalty/company/:companyCode"
        />
        <Route
          component={ProfileDelete}
          path="/personal-area/dashboard/more/profile-delete"
        />
        <Route component={Terms} path="/personal-area/dashboard/more/terms" />
        <Route
          component={Consents}
          path="/personal-area/dashboard/more/consents"
        />
        <Route
          component={DownloadPersonalData}
          path="/personal-area/dashboard/more/download-personal-data"
        />
        <Route
          component={Account}
          path="/personal-area/dashboard/more/account"
        />
        <Route
          component={ContactUs}
          path="/personal-area/dashboard/more/contact"
        />
        <Route
          exact
          component={PrivateShare}
          path="/personal-area/dashboard/more/private-share"
        />
        <Route
          exact
          component={PurchasesPage}
          path="/personal-area/dashboard/my-purchases"
        />
        <Route
          exact
          mobileOnly
          component={Notifications}
          path="/personal-area/dashboard/notifications"
        />
        {isDesktop ? (
          <Route
            component={Notifications}
            path="/personal-area/dashboard/notifications/:notificationIdHash"
          />
        ) : (
          <Route
            mobileOnly
            component={Notification}
            path="/personal-area/dashboard/notifications/:notificationIdHash"
          />
        )}
        {documentsToSigned?.length > 0 &&
          documentsToSigned
            .filter((doc) => !dialogToSignDocSeen[doc?.boligmappaNumber])
            .map((el, index) => (
              <SignDocumentDialog
                key={el?.boligmappaNumber}
                index={index}
                open={activeSignDialogIndex === index}
                onOpenChange={(open) => {
                  if (!open) {
                    onDialogToSignClose(el, index);
                  }
                }}
                onClose={() =>
                  setActiveSignDialogIndex(activeSignDialogIndex + 1)
                }
                docAvailableToSign={el}
                property={
                  ownedProperties &&
                  sharedProperties &&
                  [...ownedProperties, ...sharedProperties].find(
                    (prop) => prop.boligmappaNumber === el?.boligmappaNumber
                  )
                }
              />
            ))}
      </DashboardLayout>
    </SidebarProvider>
  );
};

const mapStateToProps = (state) => ({
  ownedProperties: sGetOwnedProperties(state),
  sharedProperties: sGetSharedPrivatelyProperties(state),
  activeUser: sGetUser(state),
  activeProperty: sGetActiveProperty(state),
  documentsToSigned: sGetDocumentsToSigned(state),
  dialogToSignDocSeen: sGetDialogToSignDocSeen(state),
  searchText: sGetDocumentSearchText(state),
  selectedPersonalFolderId: sGetPersonalSelectedFolderId(state),
  personalFoldersBreadCrumbs: sGetPersonalFoldersBreadCrumbs(state),
  unseenNotifications: sGetUnseenNotifications(state),
  propertiesLoaded: sGetPropertiesLoaded(state),
});

const mapDispatchToProps = (dispatch) => ({
  setProperties: (properties) => dispatch(acSetProperties(properties)),
  setDocumentsToSigned: (data) => dispatch(acSetDocumentsToSigned(data)),
  setDialogToSignDocSeen: (data) => dispatch(acSetDialogToSignDocSeen(data)),
  setFetchingDocumentsToSigned: (data) =>
    dispatch(acSetFetchingDocumentsToSigned(data)),
  clearDocuments: () => dispatch(acClearDocuments()),
  setFetchingProperties: (fetching) =>
    dispatch(acSetFetchingProperties(fetching)),
  setFetchingPropertiesError: (error) =>
    dispatch(acSetFetchingPropertiesError(error)),
  setSearchText: (searchText) => dispatch(acSetDocumentSearchText(searchText)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(Dashboard));
